import * as React from 'react';
import { useCallback, useState } from 'react';
import { ICellRendererParams } from '@ag-grid-community/core';

interface ImageRendererWithHoverProps extends ICellRendererParams {
  /** Used for drag and drop functionality from editable grid */
  onFileDrop?: (files: FileList) => void;
}
import { style } from 'typestyle';
import noImagePath from 'src/common-ui/images/noimage.jpg';
import { resolvePath } from 'src/cdn';
import { TEAL_PRIMARY } from 'src/common-ui/theme';
const noImage = resolvePath(noImagePath);

export const imageCellDragOverStyle = style({
  display: 'grid',
  justifyItems: 'center',
  width: '100%',
  backgroundColor: '#e3f2fd',
  border: `3px solid ${TEAL_PRIMARY}`,
  $nest: {
    img: {
      position: 'relative',
      width: '50%',
      height: 30,
      objectFit: 'contain',
      opacity: 0.7,
    },
  },
});

const styles = {
  imageHover: style({
    display: 'grid',
    justifyItems: 'center',
    width: '100%',
    $nest: {
      img: {
        position: 'relative',
        width: '50%',
        height: 30,
        objectFit: 'contain',
      },
    },
  }),
  imageHoverDragOver: imageCellDragOverStyle,
};
const ImageRendererWithHover = (props: ImageRendererWithHoverProps) => {
  const image = props.value;
  const { onFileDrop } = props;
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      if (onFileDrop && e.dataTransfer.files.length > 0) {
        onFileDrop(e.dataTransfer.files);
      }
    },
    [onFileDrop],
  );

  // this uses raw dom events because the custom tooltips in ag-grid 20.2.0 are hot garbage
  // and the support for them is very poor
  const handleMouseLeave = useCallback(() => {
    document.querySelectorAll('.hover-image').forEach((img) => {
      img.remove();
    });
  }, []);

  const handleOnMouseOver = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const existingTooltip = document.querySelector('#tooltip-image');
      if (existingTooltip?.checkVisibility()) {
        return;
      }

      const img = document.createElement('img');
      img.id = 'tooltip-image';
      img.src = image;
      img.classList.add('hover-image');
      img.setAttribute(
        'style',
        `position: absolute;
    width: 130px;
    height: 150px;
    object-fit: cover;
    top: ${e.clientY}px;
    left: ${e.clientX + 30}px;`,
      );
      document.body.appendChild(img);
    },
    [image],
  );
  return (
    <div
      className={isDragOver ? styles.imageHoverDragOver : styles.imageHover}
      onMouseOver={handleOnMouseOver}
      onMouseLeave={handleMouseLeave}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <img alt="" src={image || noImage} />
    </div>
  );
};
export default ImageRendererWithHover;
